import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { add, format, getDate, getMonth, getYear, isEqual } from 'date-fns';
import { sort } from 'remeda';
import { BehaviorSubject, combineLatest, interval, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, shareReplay, take, throttleTime } from 'rxjs/operators';
import { AuthService, isAdmin } from '../root/auth.service';

export interface AgnusRegion {
  ID: number;
  Name: string;
}

export interface CalendarData {
  startDate?: boolean;
  vacation?: boolean;
  unselectable?: boolean;
}

export interface CalendarDate extends CalendarData {
  year: number;
  month: number;
  day: number;
  id: string;
  bonusAmountCents?: number;
  hidden?: boolean;
  workingDay?: boolean;
  dayValueBonusOnlyDollars?: number;
  dayValueLinehaulAndBonusDollars?: number;
}

export interface BonusTier {
  bonusAmountCents: number;
  loadsLowerBound: number;
  loadsUpperBound: number;
}

export interface Slot {
  date: Date;
  remainingNumberOfSlots: number;
  totalNumberOfSlots: number;
}

export interface DayValue {
  date: string;
  bonusCents: number;
  lineHaulAndBonus: number;
}

interface EarningsEstimateApi {
  bonusDayCount: number;
  bonusDaysMissedPotentialCents: number;
  christmasDaysEarningCents: number;
  christmasDaysMissedPotentialCents: number;
  christmasDaysMissing: string[];
  christmasDaysTotal: number;
  christmasDaysWorking: number;
  estimatedEarningsBonusCents: number;
  estimatedEarningsCents: number;
  newYearsDaysEarningsCents: number;
  newYearsDaysMissedPotentialCents: number;
  newYearsDaysMissing: string[];
  newYearsDaysTotal: number;
  newYearsDaysWorking: number;
  totalMissedBonusPotentialCents: number;
  totalMissedPotentialCents: number;
  dayValues: Array<DayValue>;
  estLoadsOverDuration: number;
  estLoadsPerDay: number;
}

export interface EarningsEstimate {
  bonusDayCount: number;
  bonusDaysMissedPotentialCents: number;
  christmasDaysEarningCents: number;
  christmasDaysMissedPotentialCents: number;
  christmasDaysMissing: Date[];
  christmasDaysMissingPrintable: string;
  christmasDaysMissingPrintableEs: string;
  christmasDaysTotal: number;
  christmasDaysWorking: number;
  estimatedEarningsBonusCents: number;
  estimatedEarningsCents: number;
  newYearsDaysEarningsCents: number;
  newYearsDaysMissedPotentialCents: number;
  newYearsDaysMissing: Date[];
  newYearsDaysMissingPrintable: string;
  newYearsDaysMissingPrintableEs: string;
  newYearsDaysTotal: number;
  newYearsDaysWorking: number;
  totalMissedBonusPotentialCents: number;
  totalMissedPotentialCents: number;
  dayValues: Record<string, DayValue>;
  estLoadsOverDuration: number;
  estLoadsPerDay: number;
}

export interface RegistrationInfoApi {
  regionId: number | null;
  date: string | null;
  vacationDates: string[] | null;
  canRegister: boolean;
}

export interface RegistrationInfo {
  regionId: number | null;
  date: Date | null;
  vacationDates: Date[];
  canRegister: boolean;
}

export interface BonusSummary {
  loadsCompleted: number;
  raffleTicketsEarned: number;
  lineHaulAndBonusCents: number;
  bonusCents: number;
}

export interface EarningsEstimateDiffs {
  totalMissedBonusPotentialCents: number;
  totalMissedPotentialCents: number;
}

export interface CompanyDriverApi {
  id: string;
  phone: string;
  name: string;
  booked: boolean;
  disqualified: boolean;
  date: string;
  eligibilityEndAt: string;
  eligibilityStartAt: string;
  regionId: number;
  vacationDates: string[];
  canRegister: boolean;
  registrationOpensAt: string;
}

export interface CompanyDriver {
  id: string;
  phone: string;
  name: string;
  booked: boolean;
  disqualified: boolean;
  date: Date;
  eligibilityEndAt: Date;
  eligibilityStartAt: Date;
  regionId: number;
  vacationDates: Date[];
  vacationDatesStr: string;
  canRegister: boolean;
  registrationOpensAt: Date;
}

export interface CompanyRegistrationInfoApi {
  drivers: CompanyDriverApi[] | null;
  totalDrivers: number;
  bookedDrivers: number;
  registrationClosesAt: string;
}

export interface CompanyRegistrationInfo {
  drivers: CompanyDriver[];
  totalDrivers: number;
  bookedDrivers: number;
  registrationClosesAt: Date;
}

export interface LoadHistoryItem {
  bonusCents: number;
  bonusTierCents: number;
  id: number;
  lineHaulCents: number;
  lineHaulWithTakeRate: number;
  raffleCount?: number;
  uniqueId: string;
}

export interface HolidayBonusTrack {
  backgroundColor?: string;
  eligibilityStartAt: Date;
  eligibilityEndAt: Date;
  loadsToNextLevel: number;
  loadsCompletedAtCurrentLevel: number;
  bandSizeForCurrentLevel: number;
  currentBonusPerLoadInCents: number;
  nextBonusPerLoadInCents: number;
  currentTier: number;
  totalTierCount: number;
  totalCompletedLoads: number;
  awardedRaffleTicketCount: number;
  loadTrackerData: {
    linehaulAndBonusEarningsCents: number;
    onlyBonusEarningsCents: number;
    loadCompletedCount: number;
    raffleTicketCount: number;
  };
  loadHistory?: LoadHistoryItem[];
}

@Injectable({
  providedIn: 'root',
})
export class Holiday2021Service {
  private registrationEndsAt$$ = new BehaviorSubject<Date | null>(new Date('2021-12-16T06:59:59Z'));
  public registrationEndsAt$ = this.registrationEndsAt$$.pipe(shareReplay(1));
  private isOpenToAll$$ = new BehaviorSubject<boolean>(false);
  private isOpenToAllThrottle$$ = new Subject();
  private isOpenToAllShared$ = this.isOpenToAll$$.pipe(shareReplay(1));

  public get isOpenToAll$(): Observable<boolean> {
    this.isOpenToAllThrottle$$.next(null);
    return this.isOpenToAllShared$;
  }

  private language$$ = new BehaviorSubject<'en' | 'es'>('en');
  public language$ = this.language$$.pipe(shareReplay(1));
  private driverId$$ = new BehaviorSubject<string | null>(null);
  public driverId$ = this.driverId$$.pipe(shareReplay(1));
  public selectedDriver$: Observable<CompanyDriver>;
  private companyRegistrationInfo$$ = new BehaviorSubject<CompanyRegistrationInfo | null>(null);
  public companyRegistrationInfo$ = this.companyRegistrationInfo$$.pipe(shareReplay(1));
  public isAdmin$: Observable<boolean>;
  private registrationInfo$$ = new BehaviorSubject<RegistrationInfo | null>(null);
  public registrationInfo$ = this.registrationInfo$$.pipe(shareReplay(1));
  private trackerInfo$$ = new BehaviorSubject<HolidayBonusTrack | null>(null);
  public trackerInfo$ = this.trackerInfo$$.pipe(shareReplay(1));
  private bonusSummary$$ = new BehaviorSubject<BonusSummary | null>({
    loadsCompleted: 0,
    raffleTicketsEarned: 0,
    lineHaulAndBonusCents: 0,
    bonusCents: 0,
  });
  public bonusSummary$ = this.bonusSummary$$.pipe(shareReplay(1));
  private earningsEstimate$$ = new BehaviorSubject<EarningsEstimate | null>(null);
  public earningsEstimate$ = this.earningsEstimate$$.pipe(shareReplay(1));
  private slots$$ = new BehaviorSubject<Slot[]>([]);
  public slots$ = this.slots$$.pipe(shareReplay(1));
  private canRegister$$ = new BehaviorSubject<boolean>(false);
  public canRegister$ = this.canRegister$$.pipe(shareReplay(1));
  private selectedAgnusRegionId$$ = new BehaviorSubject<number | null>(null);
  public selectedAgnusRegionId$ = this.selectedAgnusRegionId$$.pipe(shareReplay(1));
  private bonusTiers$$ = new BehaviorSubject<BonusTier[]>(null);
  public bonusTiers$ = this.bonusTiers$$.pipe(shareReplay(1));
  private agnusRegions$$ = new BehaviorSubject<AgnusRegion[]>([]);
  public agnusRegions$ = this.agnusRegions$$.pipe(shareReplay(1));
  public selectedAgnusRegion$: Observable<AgnusRegion>;
  private calendar$$ = new BehaviorSubject<Array<CalendarDate>>([]);
  public calendar$: Observable<CalendarDate[]> = this.calendar$$.pipe(shareReplay(1));
  public startDate$: Observable<Date | null> = this.calendar$.pipe(
    map((cal) => {
      const startDate = cal.find((c) => c.startDate);
      if (!startDate) {
        return null;
      }
      return new Date(startDate.year, startDate.month - 1, startDate.day);
    }),
  );
  public endDate$: Observable<Date | null> = this.startDate$.pipe(map((date) => add(date, { days: 20 })));
  public canBook$: Observable<boolean> = combineLatest([this.selectedAgnusRegionId$, this.startDate$]).pipe(
    map(([a, b]) => {
      return !!a && !!b;
    }),
  );
  public december$ = this.calendar$.pipe(
    map((cal) => cal.filter((c) => c.year === 2021)),
    map((cal) =>
      cal.map((c) =>
        c.month === 12
          ? c
          : {
              ...c,
              hidden: true,
            },
      ),
    ),
  );
  public january$ = this.calendar$.pipe(
    map((cal) => cal.filter((c) => c.year === 2022 || (c.month === 12 && c.day >= 26))),
    map((cal) =>
      cal.map((c) =>
        c.year === 2022
          ? c
          : {
              ...c,
              hidden: true,
            },
      ),
    ),
  );

  public vacationDays$: Observable<Date[]> = this.calendar$.pipe(
    map((cal) => {
      return (cal || []).filter((c) => c.vacation).map((c) => new Date(c.year, c.month - 1, c.day));
    }),
  );

  constructor(
    private httpClient: HttpClient,
    private authService: AuthService, // private featureFlagService: FeatureFlagService,
  ) {
    this.makeCalendar();
    this.setupBonusInfo();
    this.loadRegions(); // Nothing to refresh here, once and be done with it
    this.setupSelectedRegion();
    this.listenToAgnusRegionId();
    this.listenToVacationDayChanges();
    this.setupSelectedDriver();
    this.isAdmin$ = this.authService.userInfo$.pipe(map(isAdmin));
    this.isAdmin$.pipe(take(1)).subscribe(() => {
      this.loadCompanyInfo();
    });
    const language: 'en' | 'es' = localStorage.getItem('2021_holiday_language') as any;
    if (language) {
      this.language$$.next(language);
    }
    this.isOpenToAllThrottle$$.pipe(throttleTime(60 * 1000)).subscribe({
      next: () => {
        // Once it is open to all, we don't need to refresh anymore
        if (this.isOpenToAll$$.value) {
          return;
        }
        this.getIsOpenToAll();
      },
    });
    interval(1000 * 60).subscribe({
      next: () => {
        this.isOpenToAllThrottle$$.next();
      },
    });
    this.getRegistrationEndsAt();
  }

  public setStartDate(date: Date) {
    const regionID = this.selectedAgnusRegionId$$.value;
    if (!regionID) {
      return;
    }
    const dayCount = regionID === 3 || regionID === 12 ? 15 : 20;
    const existingDaysCopy = [...this.calendar$$.value].map((c) => {
      return {
        ...c,
        startDate: undefined,
        unselectable: undefined,
        workingDay: undefined,
        vacation: undefined,
      };
    });
    if (date !== null) {
      const index = this.calendar$$.value.findIndex((c) => c.month === getMonth(date) + 1 && c.day === getDate(date));
      if (index === -1) {
        return;
      }

      existingDaysCopy[index].startDate = true;
      existingDaysCopy[index].unselectable = true;
      for (let i = index; i < Math.min(index + dayCount, existingDaysCopy.length); i++) {
        existingDaysCopy[i].workingDay = true;
      }
    }

    this.calendar$$.next(existingDaysCopy);
  }

  public async setDriverId(id: string) {
    if (await this.isAdmin$.pipe(take(1)).toPromise()) {
      this.driverId$$.next(id);
    }
  }

  public setLanguage(language: 'en' | 'es') {
    localStorage.setItem('2021_holiday_language', language);
    this.language$$.next(language);
  }

  public toggleVacationDay(year: number, month: number, day: number, loadBonuses = false) {
    const cDay = this.calendar$$.value.find((c) => c.year === year && c.month === month && c.day === day);
    if (!cDay) {
      return;
    }
    this.updateDay(month, day, { vacation: !cDay.vacation });
    this.getBonusInfo();
  }

  public selectRegion(id: number | null) {
    const startDate = this.calendar$$.value.find((c) => c.startDate);
    this.bonusTiers$$.next(null);
    this.calendar$$.next(
      this.calendar$$.value.map((c) => ({
        ...c,
        bonusAmountCents: undefined,
      })),
    );
    this.selectedAgnusRegionId$$.next(id);
    if (startDate) {
      this.setStartDate(new Date(startDate.year, startDate.month - 1, startDate.day));
    }
  }

  public async findSlots() {
    const agnusRegionID = this.selectedAgnusRegionId$$.value;
    if (!agnusRegionID) {
      return;
    }
    const response = await this.httpClient
      .get<{ slots: Slot[] }>(`${environment.api}/v2/holiday_event/open_slots`, {
        params: {
          regionId: `${agnusRegionID}`,
        },
      })
      .toPromise();
    this.slots$$.next(
      (response.slots || []).map((slot) => ({
        ...slot,
        date: new Date(((slot.date as any) as string).replace('Z', '')),
      })),
    );
  }

  private makeCalendar() {
    const calendar: Array<CalendarDate | null> = [];
    // december
    for (let i = 12; i <= 31; i++) {
      calendar.push({
        year: 2021,
        month: 12,
        day: i,
        id: `2021-12-${i}`,
      });
    }
    // january
    for (let i = 1; i <= 22; i++) {
      calendar.push({
        year: 2022,
        month: 1,
        day: i,
        id: `2022-1-${i}`,
      });
    }
    this.calendar$$.next(calendar);
  }

  private updateDay(month: number, day: number, data: Partial<CalendarData>) {
    const existingDaysCopy = [...this.calendar$$.value];
    const index = existingDaysCopy.findIndex((c) => c.month === month && c.day === day);
    if (index === -1) {
      return;
    }
    const newEntry: CalendarDate = {
      ...existingDaysCopy[index],
      ...data,
    };
    existingDaysCopy[index] = newEntry;
    this.calendar$$.next(existingDaysCopy);
  }

  private listenToAgnusRegionId() {
    this.selectedAgnusRegion$.subscribe({
      next: (id) => {
        if (!id) {
          return;
        }
        this.loadBonusTiers();
        this.findSlots();
      },
    });
  }

  private async loadBonusTiers() {
    const agnusRegionID = this.selectedAgnusRegionId$$.value;
    if (!agnusRegionID) {
      return;
    }
    const response = await this.httpClient
      .get<{
        bonusTiers: BonusTier[];
        canRegister: boolean;
        christmasAcuteDays: string[];
        newYearsAcuteDays: string[];
      }>(`${environment.api}/v2/holiday_event/booking_info`, {
        params: {
          regionId: `${agnusRegionID}`,
        },
      })
      .toPromise();
    this.bonusTiers$$.next(response.bonusTiers);
    this.canRegister$$.next(response.canRegister);
    const acuteMap = acuteDaysToMap(response.christmasAcuteDays, response.newYearsAcuteDays);
    const calCopy = [...this.calendar$$.value].map((cd) => {
      const dateKey = `${cd.year}-${cd.month.toString().padStart(2, '0')}-${cd.day.toString().padStart(2, '0')}`;
      if (acuteMap[dateKey]) {
        return {
          ...cd,
          bonusAmountCents: 10000,
        };
      }
      return cd;
    });
    this.calendar$$.next(calCopy);
  }

  private setupSelectedRegion() {
    this.selectedAgnusRegion$ = combineLatest([this.selectedAgnusRegionId$, this.agnusRegions$]).pipe(
      map(([id, regions]) => {
        if (!id || !regions || !regions.length) {
          return null;
        }
        return regions.find((r) => r.ID === id);
      }),
    );
  }

  private setupSelectedDriver() {
    this.selectedDriver$ = combineLatest([this.driverId$, this.companyRegistrationInfo$]).pipe(
      map(([driverId, cri]) => {
        if (!driverId || !cri?.drivers) {
          return null;
        }
        return cri.drivers.find((d) => d.id === driverId);
      }),
    );

    this.driverId$.pipe(distinctUntilChanged()).subscribe({
      next: (id) => {
        if (id) {
          this.getUserRegistrationInfo();
        }
      },
    });
  }

  private async loadRegions() {
    // const response = await this.httpClient
    //   .get<{ loHiRegions: AgnusRegion[] }>(`${environment.api}/v2/admin/agnus_regions`)
    //   .pipe(map((resp) => resp.loHiRegions))
    //   .toPromise();
    // const featureFlags = await this.featureFlagService.flags$
    //   .pipe(
    //     filter((ff) => !!Object.keys(ff).length),
    //     take(1),
    //   )
    //   .toPromise();
    const regions = [
      {
        Name: 'North Dakota',
        ID: 3,
      },
      {
        Name: 'West TX and NM',
        ID: 4,
      },
      {
        Name: 'South TX',
        ID: 5,
      },
      {
        Name: 'East TX and LA',
        ID: 6,
      },
      {
        Name: 'Oklahoma',
        ID: 7,
      },
      {
        Name: 'WV and Ohio',
        ID: 9,
      },
      {
        Name: 'CO and WY',
        ID: 12,
      },
    ];
    // if (!environment.production) {
    //   regions.push({
    //     Name: '---STAGE ONLY--- TESTING REGION',
    //     ID: 12,
    //   });
    // }
    this.agnusRegions$$.next(regions);
  }

  private setupBonusInfo() {
    this.startDate$.pipe(distinctUntilChanged(isEqual)).subscribe({
      next: () => {
        this.getBonusInfo();
      },
    });

    this.selectedAgnusRegionId$.pipe(debounceTime(100)).subscribe({
      next: () => {
        this.getBonusInfo();
      },
    });
  }

  private getBookingInfo(): { regionId: number; date: Date; vacationDates: Date[]; userId?: string } {
    const agnusRegionID = this.selectedAgnusRegionId$$.value;
    if (!agnusRegionID) {
      return null;
    }
    const startDay = this.calendar$$.value.find((cd) => cd.startDate);
    if (!startDay) {
      return null;
    }
    const startDate = new Date(Date.UTC(startDay.year, startDay.month - 1, startDay.day));
    const vacationDays = this.calendar$$.value.filter((cd) => cd.vacation);
    const vacationDates = vacationDays.map((d) => new Date(Date.UTC(d.year, d.month - 1, d.day)));
    return {
      regionId: agnusRegionID,
      date: startDate,
      vacationDates,
    };
  }

  private async getBonusInfo() {
    const bookingInfo = this.getBookingInfo();
    if (!bookingInfo) {
      return;
    }
    const response = await this.httpClient
      .post<EarningsEstimateApi>(`${environment.api}/v2/holiday_event/earnings_estimate`, bookingInfo)
      .toPromise();
    const ee = formatEarningsEstimate(response);
    this.earningsEstimate$$.next(ee);
    if (ee?.dayValues) {
      const calendarCopy = [...this.calendar$$.value].map((cal) => {
        const dateKey = `${cal.year}-${cal.month.toString().padStart(2, '0')}-${cal.day.toString().padStart(2, '0')}`;
        return {
          ...cal,
          dayValueBonusOnlyDollars: Math.round(ee.dayValues[dateKey]?.bonusCents / 100),
          dayValueLinehaulAndBonusDollars: Math.round(ee.dayValues[dateKey]?.lineHaulAndBonus / 100),
        };
      });
      this.calendar$$.next(calendarCopy);
    }
  }

  public async book(): Promise<null> {
    const bookingInfo = this.getBookingInfo();
    if (!bookingInfo) {
      return;
    }
    if (this.driverId$$.value) {
      bookingInfo.userId = this.driverId$$.value;
    }
    return this.httpClient.post<null>(`${environment.api}/v2/holiday_event/book_slot`, bookingInfo).toPromise();
  }

  private listenToVacationDayChanges() {
    // this.earningsEstimateDiffs$.subscribe({
    //   next: (diffs) => {
    //     if (!diffs || diffs.totalMissedBonusPotentialCents === 0) {
    //       return;
    //     }
    //     if (!this.lastVacationDaySelected$$.value) {
    //       return;
    //     }
    //     const vDay = this.lastVacationDaySelected$$.value;
    //     if (diffs.totalMissedPotentialCents !== 0) {
    //       this.snackBar.openFromComponent(HolidayTwentyoneVacationDaySnackbarComponent, {
    //         data: {
    //           diff: diffs.totalMissedPotentialCents,
    //           date: vDay,
    //         },
    //         panelClass: [diffs.totalMissedPotentialCents > 0 ? 'bg-salmon-400' : 'bg-green-400', 'z-20', 'h-8'],
    //         duration: 1500,
    //         verticalPosition: 'top',
    //       });
    //     }
    //   },
    // });
  }

  public async getUserRegistrationInfo() {
    const params: {
      [param: string]: string | string[];
    } = {};
    const ghostPhone = localStorage.getItem('holiday_2021_ghost_phone');
    if (ghostPhone) {
      params.ghostPhone = ghostPhone;
    }
    if (this.driverId$$.value) {
      params.userId = this.driverId$$.value;
    }
    const result = await this.httpClient
      .get<RegistrationInfoApi>(`${environment.api}/v2/holiday_event/registration_info`, {
        params,
      })
      .toPromise();
    const formatted = formatRegistrationInfo(result);
    this.selectedAgnusRegionId$$.next(formatted.regionId);
    this.setStartDate(formatted.date);

    formatted.vacationDates.forEach((date) => {
      this.toggleVacationDay(getYear(date), getMonth(date) + 1, getDate(date));
    });
    this.registrationInfo$$.next(formatRegistrationInfo(result));
  }

  public async loadCompanyInfo() {
    if (!(await this.isAdmin$.pipe(take(1)).toPromise())) {
      return;
    }
    const result = await this.httpClient
      .get<CompanyRegistrationInfoApi>(`${environment.api}/v2/holiday_event/company_registration_info`)
      .toPromise();
    this.companyRegistrationInfo$$.next(formatCompanyInfo(result));
  }

  private getIsOpenToAll() {
    this.httpClient.get<{ openToAll: boolean }>(`${environment.api}/v2/holiday_event/open_to_all`).subscribe({
      next: (resp) => {
        this.isOpenToAll$$.next(resp.openToAll);
      },
    });
  }

  private getRegistrationEndsAt() {
    this.httpClient
      .get<{ registrationClosesAt: string }>(`${environment.api}/v2/holiday_event/registration_ends`)
      .subscribe({
        next: (resp) => {
          this.registrationEndsAt$$.next(resp?.registrationClosesAt ? new Date(resp.registrationClosesAt) : null);
        },
      });
  }

  public getEventTrackInfo() {
    const ghostPhone = localStorage.getItem('holiday_2021_ghost_phone');
    const params: {
      [param: string]: string | string[];
    } = {};
    if (ghostPhone) {
      params.ghostPhone = ghostPhone;
    }
    this.httpClient
      .get<HolidayBonusTrack>(`${environment.api}/v2/holiday_event/event_tracker`, {
        params,
      })
      .subscribe({
        next: (resp) => {
          this.trackerInfo$$.next({
            ...resp,
            eligibilityStartAt: new Date(((resp.eligibilityStartAt as any) as string).replace('Z', '')),
            eligibilityEndAt: new Date(((resp.eligibilityEndAt as any) as string).replace('Z', '')),
          });
        },
      });
  }
}

function formatEarningsEstimate(ee: EarningsEstimateApi): EarningsEstimate {
  const christmasDaysMissing = (ee.christmasDaysMissing || []).map((d) => new Date(d.replace('Z', '')));
  const newYearsDaysMissing = (ee.newYearsDaysMissing || []).map((d) => new Date(d.replace('Z', '')));
  const dateOptions = { month: 'short', day: 'numeric' };
  return {
    ...ee,
    christmasDaysMissing,
    newYearsDaysMissing,
    christmasDaysMissingPrintableEs: christmasDaysMissing.length
      ? christmasDaysMissing.map((d) => d.toLocaleDateString('es-ES', dateOptions)).join(', ')
      : null,
    christmasDaysMissingPrintable: christmasDaysMissing.length
      ? christmasDaysMissing.map((d) => format(d, 'MMM do')).join(', ')
      : null,
    newYearsDaysMissingPrintable: newYearsDaysMissing.length
      ? newYearsDaysMissing.map((d) => format(d, 'MMM do')).join(', ')
      : null,
    newYearsDaysMissingPrintableEs: newYearsDaysMissing.length
      ? newYearsDaysMissing.map((d) => d.toLocaleDateString('es-ES', dateOptions)).join(', ')
      : null,
    dayValues: dayValuesArrayToMap(ee.dayValues),
  };
}

function dayValuesArrayToMap(dv: DayValue[]): Record<string, DayValue> {
  const response: Record<string, DayValue> = {};
  dv.forEach((d) => {
    response[d.date.split('T')[0]] = d;
  });
  return response;
}

function acuteDaysToMap(christmasDays: string[], newYearsDays: string[]): Record<string, boolean> {
  const response: Record<string, boolean> = {};
  (christmasDays || []).forEach((d) => {
    response[d.split('T')[0]] = true;
  });
  (newYearsDays || []).forEach((d) => {
    response[d.split('T')[0]] = true;
  });
  return response;
}

function formatRegistrationInfo(ri: RegistrationInfoApi): RegistrationInfo {
  return {
    ...ri,
    date: ri.date ? new Date(ri.date.replace('Z', '')) : null,
    vacationDates: (ri.vacationDates || []).map((dStr) => new Date(dStr.replace('Z', ''))),
  };
}

function formatCompanyInfo(ci: CompanyRegistrationInfoApi): CompanyRegistrationInfo {
  return {
    ...ci,
    drivers: sort((ci.drivers || []).map(formatCompanyDriver), (a, b) =>
      a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
    ),
    registrationClosesAt: new Date(ci.registrationClosesAt.replace('Z', '')),
  };
}

function formatCompanyDriver(dr: CompanyDriverApi): CompanyDriver {
  const vacationDates = (dr.vacationDates || []).map((vd) => new Date(vd.replace('Z', '')));
  return {
    ...dr,
    date: dr.date ? new Date(dr.date.replace('Z', '')) : null,
    eligibilityEndAt: dr.eligibilityEndAt ? new Date(dr.eligibilityEndAt.replace('Z', '')) : null,
    eligibilityStartAt: dr.eligibilityStartAt ? new Date(dr.eligibilityStartAt.replace('Z', '')) : null,
    vacationDates,
    vacationDatesStr: vacationDates.map((cd) => format(cd, 'MMM do')).join(', '),
    registrationOpensAt: new Date(dr.registrationOpensAt.replace('Z', '')),
  };
}
